<template>
  <div>
    <!-- Begin page -->
    <div id="layout-wrapper">
      <NavigationBar />
      <!-- ============================================================== -->
      <!-- Start right Content here -->
      <!-- ============================================================== -->
      <div class="content-minimize">
        <div class="page-content">
          <div class="container-fluid">
            <!-- start page title -->
            <div class="row">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">Employee Management Dashboard</h4>
                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a href="index.html">Home</a></li>
                      <li class="breadcrumb-item active">Employees</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
            <!-- end page title -->

            <!-- Enhanced stats cards -->
            <div class="row">
              <!-- Total Employees -->
              <div class="col-xl-3 col-md-6 mb-4">
                <div class="card mini-stats-wid h-100">
                  <div class="card-body">
                    <div class="d-flex flex-wrap">
                      <div class="me-3">
                        <p class="text-muted mb-2">Total Employees</p>
                        <h3 class="mb-0 text-primary">{{totalItems}}</h3>
                        <small class="text-muted">All registered staff</small>
                      </div>
                      <div class="avatar-sm ms-auto">
                        <div class="avatar-title bg-primary bg-gradient rounded-circle text-white font-size-20">
                          <i class="mdi mdi-account-group"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Active Employees -->
              <div class="col-xl-3 col-md-6 mb-4">
                <div class="card mini-stats-wid h-100">
                  <div class="card-body">
                    <div class="d-flex flex-wrap">
                      <div class="me-3">
                        <p class="text-muted mb-2">Active Employees</p>
                        <h3 class="mb-0 text-success">{{activeEmployees}}</h3>
                        <small class="text-muted">Currently working</small>
                      </div>
                      <div class="avatar-sm ms-auto">
                        <div class="avatar-title bg-success bg-gradient rounded-circle text-white font-size-20">
                          <i class="mdi mdi-account-check"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Permanent Employees -->
              <div class="col-xl-3 col-md-6 mb-4">
                <div class="card blog-stats-wid h-100">
                  <div class="card-body">
                    <div class="d-flex flex-wrap">
                      <div class="me-3">
                        <p class="text-muted mb-2">Permanent Staff</p>
                        <h3 class="mb-0 text-info">{{permanentEmployees}}</h3>
                        <small class="text-muted">Full-time employees</small>
                      </div>
                      <div class="avatar-sm ms-auto">
                        <div class="avatar-title bg-info bg-gradient rounded-circle text-white font-size-20">
                          <i class="mdi mdi-briefcase-check"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Departments -->
              <div class="col-xl-3 col-md-6 mb-4">
                <div class="card blog-stats-wid h-100">
                  <div class="card-body">
                    <div class="d-flex flex-wrap">
                      <div class="me-3">
                        <p class="text-muted mb-2">Departments</p>
                        <h3 class="mb-0 text-warning">{{departments.length}}</h3>
                        <small class="text-muted">Active departments</small>
                      </div>
                      <div class="avatar-sm ms-auto">
                        <div class="avatar-title bg-warning bg-gradient rounded-circle text-white font-size-20">
                          <i class="mdi mdi-office-building"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Additional quick stats -->
            <div class="row">
              <div class="col-12">
                <div class="card mb-4">
                  <div class="card-body">
                    <div class="row text-center">
                      <div class="col-md-3 col-6 border-end">
                        <h6 class="text-muted mb-1">New This Month</h6>
                        <h4 class="mb-0">{{newEmployeesThisMonth}}</h4>
                      </div>
                      <div class="col-md-3 col-6 border-end">
                        <h6 class="text-muted mb-1">Contract Staff</h6>
                        <h4 class="mb-0">{{contractEmployees}}</h4>
                      </div>
                      <div class="col-md-3 col-6 border-end">
                        <h6 class="text-muted mb-1">Internships</h6>
                        <h4 class="mb-0">{{internshipEmployees}}</h4>
                      </div>
                      <div class="col-md-3 col-6">
                        <h6 class="text-muted mb-1">Average Tenure</h6>
                        <h4 class="mb-0">{{averageTenure}} years</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Main employee table -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-white pt-0 pr-0 p-0 d-flex justify-content-between align-items-center w-100 border-bottom">
                    <div class="btn-toolbar p-3 d-flex justify-content-between align-items-center w-100" role="toolbar">
                      <div class="flex-grow-1">
                        <div class="search-box mb-0 me-3">
                          <div class="position-relative">
                            <i v-if="!loading" class="bx bx-search-alt search-icon"></i>
                            <div v-else class="search-icon">
                              <span class="spinner-border spinner-border-sm text-primary" role="status"></span>
                            </div>
                            <input v-model="search"
                                   @input="debounceSearch"
                                   @keyup.enter="getEmployees"
                                   type="text"
                                   class="form-control bg-light border-light rounded"
                                   :class="{'is-loading': loading}"
                                   placeholder="Search by name, ID, email, phone, or department..."
                                   spellcheck="false">
                            <button @click="getEmployees"
                                    class="btn btn-link search-btn"
                                    type="button"
                                    :disabled="loading">
                              <i class="bx bx-search-alt"></i>
                            </button>
                            <button v-if="search"
                                    @click="clearSearch"
                                    class="btn btn-link clear-btn"
                                    type="button">
                              <i class="mdi mdi-close"></i>
                            </button>
                          </div>
                        </div>
                      </div>

                      <div class="d-flex flex-wrap">
                        <div class="d-flex align-items-center me-3 pe-3">
                          <div>
                            <div class="float-end d-flex">
                              <div class="input-group fw-semibold me-2">
                                <label class="input-group-text">Department</label>
                                <select v-model="departmentFilter" @change="onFilterChange" class="form-select" style="min-width: 150px;">
                                  <option selected value="">All</option>
                                  <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                                    {{ dept.departmentName }}
                                  </option>
                                </select>
                              </div>

                              <div class="input-group fw-semibold me-2">
                                <label class="input-group-text">Status</label>
                                <select v-model="statusFilter" @change="onFilterChange" class="form-select">
                                  <option selected value="">All</option>
                                  <option value="active">Active</option>
                                  <option value="resigned">Resigned</option>
                                </select>
                              </div>

                              <div class="input-group fw-semibold me-2">
                                <label class="input-group-text">Type</label>
                                <select v-model="typeFilter" @change="onFilterChange" class="form-select">
                                  <option selected value="">All</option>
                                  <option value="Permanent">Permanent</option>
                                  <option value="Contract">Contract</option>
                                  <option value="Temporary">Temporary</option>
                                  <option value="Internship">Internship</option>
                                </select>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- NEW: Category & Roles Button -->
                        <button @click="goToCategoryManagement" type="button"
                                class="btn btn-info option-selector me-2">
                          <i class="mdi mdi-shield-account"></i>
                          <span class="pl-1 d-md-inline">Category & Roles</span>
                        </button>

                        <button @click="downloadEmployees" type="button"
                                class="btn btn-outline-primary option-selector me-2"
                                :class="{'btn-loading': isDownloading}"
                                :disabled="isDownloading || loading || employees.length === 0">
                          <i :class="isDownloading ? 'mdi mdi-loading mdi-spin' : 'mdi mdi-file-excel'"></i>
                          <span class="pl-1 d-md-inline">Export</span>
                        </button>

                        <button @click="gotTo('employee-bulk-upload')" type="button"
                                class="btn btn-outline-info option-selector me-2">
                          <i class="mdi mdi-upload"></i>
                          <span class="pl-1 d-md-inline">Bulk Upload</span>
                        </button>

                        <a @click="gotTo('employee-create')" href="javascript: void(0);" type="button"
                           class="btn btn-success option-selector">
                          <i class="mdi mdi-plus-circle-outline font-size-16"></i>
                          <span class="pl-1 d-md-inline">Add Employee</span>
                        </a>
                      </div>
                    </div>
                  </div>

                  <div class="card-body">
                    <div v-if="loading" class="text-center p-5">
                      <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                      <p class="mt-2">Loading employee data...</p>
                    </div>

                    <div v-else-if="employees && employees.length === 0" class="text-center p-5">
                      <div class="avatar-md mx-auto mb-4">
                        <div class="avatar-title bg-light rounded-circle text-primary">
                          <i class="mdi mdi-account-search font-size-24"></i>
                        </div>
                      </div>
                      <h5 class="mt-4">No employees found</h5>
                      <p class="text-muted">Try adjusting your search or filters</p>
                      <button @click="resetFilters" class="btn btn-outline-primary mt-3">Reset Filters</button>
                    </div>

                    <div v-else class="table-responsive">
                      <table class="table align-middle table-hover contacts-table text-nowrap">
                        <thead class="table-light">
                        <tr>
                          <th scope="col" style="width: 50px;">
                            <div class="form-check">
                              <input class="form-check-input" type="checkbox" v-model="selectAll" @change="toggleSelectAll">
                            </div>
                          </th>
                          <th scope="col">Employee</th>
                          <th scope="col">Contact Info</th>
                          <th scope="col">Department & Role</th>
                          <th scope="col">Employment Details</th>
                          <th scope="col">Personal Info</th>
                          <th scope="col">Status</th>
                          <th scope="col" class="text-center">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(item, index) in employees" :key="index"
                            :class="{'table-selected': selectedEmployees.includes(item.id)}">
                          <td>
                            <div class="form-check">
                              <input class="form-check-input" type="checkbox"
                                     :value="item.id" v-model="selectedEmployees">
                            </div>
                          </td>
                          <td>
                            <a @click="viewDetails(item)" href="javascript: void(0)" class="d-flex align-items-center">
                              <div class="avatar-sm me-3">
                                <span class="avatar-title rounded-circle bg-primary bg-gradient text-white">
                                  {{getInitials(item.firstName, item.lastName)}}
                                </span>
                              </div>
                              <div>
                                <h6 class="mb-0">{{getFullName(item)}}</h6>
                                <small class="text-muted">{{item.empNo || 'N/A'}}</small>
                              </div>
                            </a>
                          </td>
                          <td>
                            <div>
                              <div class="mb-1">
                                <i class="mdi mdi-email-outline text-muted me-1"></i>
                                <a :href="'mailto:' + item.email" class="text-dark">
                                  {{item.email || 'N/A'}}
                                </a>
                              </div>
                              <div>
                                <i class="mdi mdi-phone-outline text-muted me-1"></i>
                                <span>{{formatPhone(item.phone)}}</span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div>
                              <span class="badge bg-info mb-1">{{item.departmentName || 'N/A'}}</span>
                              <br>
                              <small class="text-muted">ID: {{item.departmentID}}</small>
                            </div>
                          </td>
                          <td>
                            <div>
                              <div class="mb-1">
                                <span :class="getEmploymentTypeClass(item.employmentType)">
                                  {{item.employmentType || 'N/A'}}
                                </span>
                              </div>
                              <small class="text-muted">
                                <i class="mdi mdi-calendar-check me-1"></i>
                                Joined: {{formatDate(item.hireDate)}}
                              </small>
                              <br>
                              <small class="text-muted">
                                Tenure: {{calculateTenure(item.hireDate)}}
                              </small>
                            </div>
                          </td>
                          <td>
                            <div>
                              <div class="mb-1">
                                <small class="text-muted">ID: {{item.nationalId || 'N/A'}}</small>
                              </div>
                              <div class="mb-1">
                                <small class="text-muted">
                                  <i class="mdi mdi-gender-{{getGenderIcon(item.gender)}} me-1"></i>
                                  {{item.gender || 'Not specified'}}
                                </small>
                              </div>
                              <div>
                                <small class="text-muted">
                                  <i class="mdi mdi-flag me-1"></i>
                                  {{item.nationality || 'N/A'}}
                                </small>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div>
                              <span :class="getStatusClass(item.status)">
                                {{getStatusText(item.status)}}
                              </span>
                              <br>
                              <small class="text-muted">
                                Updated: {{humanDateTimeAgo(item.updatedAt)}}
                              </small>
                            </div>
                          </td>
                          <td class="text-center">
                            <div class="btn-group" role="group">
                              <button @click="viewDetails(item)" type="button" class="btn btn-sm btn-outline-primary" title="View Details">
                                <i class="mdi mdi-eye"></i>
                              </button>
                              <button @click="editEmployee(item)" type="button" class="btn btn-sm btn-outline-info" title="Edit">
                                <i class="mdi mdi-pencil"></i>
                              </button>
                              <div class="btn-group" role="group">
                                <button type="button" class="btn btn-sm btn-outline-secondary dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                                  <i class="mdi mdi-dots-vertical"></i>
                                </button>
                                <ul class="dropdown-menu">
                                  <li>
                                    <a @click="viewUserAccount(item)"
                                       class="dropdown-item"
                                       href="javascript:void(0)"
                                       data-bs-toggle="modal"
                                       data-bs-target=".user-management-modal">
                                      <i class="mdi mdi-account-cog me-2 text-primary"></i>Manage User Account
                                    </a>
                                  </li>
                                  <li><hr class="dropdown-divider"></li>
                                  <li><a class="dropdown-item" href="javascript:void(0)">
                                    <i class="mdi mdi-file-document-outline me-2"></i>Generate Report
                                  </a></li>
                                  <li><a class="dropdown-item" href="javascript:void(0)">
                                    <i class="mdi mdi-history me-2"></i>View History
                                  </a></li>
                                  <li><a class="dropdown-item" href="javascript:void(0)">
                                    <i class="mdi mdi-email-send me-2"></i>Send Email
                                  </a></li>
                                  <li><hr class="dropdown-divider"></li>
                                  <li v-if="item.status === 'Active'">
                                    <a class="dropdown-item text-warning" href="javascript:void(0)">
                                      <i class="mdi mdi-account-off me-2"></i>Deactivate
                                    </a>
                                  </li>
                                  <li v-else>
                                    <a class="dropdown-item text-success" href="javascript:void(0)">
                                      <i class="mdi mdi-account-check me-2"></i>Activate
                                    </a>
                                  </li>
                                  <li><hr class="dropdown-divider"></li>
                                  <li><a class="dropdown-item text-danger" href="javascript:void(0)">
                                    <i class="mdi mdi-delete me-2"></i>Delete
                                  </a></li>
                                </ul>
                              </div>
                            </div>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- Bulk Actions Bar (shown when items are selected) -->
                    <div v-if="selectedEmployees.length > 0" class="bg-light p-3 rounded mt-3">
                      <div class="d-flex justify-content-between align-items-center">
                        <span class="text-muted">
                          <strong>{{selectedEmployees.length}}</strong> employee(s) selected
                        </span>
                        <div>
                          <button class="btn btn-sm btn-outline-primary me-2">
                            <i class="mdi mdi-email"></i> Send Email
                          </button>
                          <button class="btn btn-sm btn-outline-info me-2">
                            <i class="mdi mdi-file-export"></i> Export Selected
                          </button>
                          <button class="btn btn-sm btn-outline-warning me-2">
                            <i class="mdi mdi-account-edit"></i> Bulk Edit
                          </button>
                          <button class="btn btn-sm btn-outline-danger">
                            <i class="mdi mdi-delete"></i> Delete Selected
                          </button>
                        </div>
                      </div>
                    </div>

                    <!-- Pagination -->
                    <div class="row mt-4">
                      <div class="col-sm-12 col-md-5">
                        <div class="dataTables_info">
                          Showing {{startIndex + 1}} to {{Math.min(endIndex, totalItems)}} of {{totalItems}} entries
                          <span v-if="search || departmentFilter || statusFilter || typeFilter" class="text-muted">
                            (filtered)
                          </span>
                        </div>
                      </div>
                      <div class="col-sm-12 col-md-7">
                        <div class="dataTables_paginate paging_simple_numbers">
                          <ul class="pagination justify-content-end mb-0">
                            <li class="page-item" :class="{ disabled: currentPage === 1 }">
                              <a class="page-link" href="javascript:void(0)" @click="prevPage" aria-label="Previous">
                                <span aria-hidden="true">&laquo;</span>
                              </a>
                            </li>
                            <li v-for="page in pagination(currentPage, totalPages, 5)" :key="page"
                                class="page-item" :class="{ active: page === currentPage, disabled: page === '...' }">
                              <a class="page-link" href="javascript:void(0)" @click="goToPage(page)">{{page}}</a>
                            </li>
                            <li class="page-item" :class="{ disabled: currentPage === totalPages || totalPages === 0 }">
                              <a class="page-link" href="javascript:void(0)" @click="nextPage" aria-label="Next">
                                <span aria-hidden="true">&raquo;</span>
                              </a>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- end col -->
            </div>
            <!-- end row -->
          </div>
          <!-- container-fluid -->
        </div>
        <!-- End Page-content -->

        <footer class="footer">
          <div class="container-fluid">
            <div class="row">
              <div class="col-sm-6">
                © 2025 Employee Management System | {{totalItems}} Total Employees
              </div>
              <div class="col-sm-6">
                <div class="text-sm-end d-sm-block">
                  Crafted with <i class="mdi mdi-heart text-danger"></i> by HR Team
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
      <!-- end main content-->
    </div>
    <!-- end main content-->

    <!-- Modals -->
    <UserManagementModal :employee="selectedEmployee || {}" />
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import UserManagementModal from "@/components/employeeRegister/modals/UserManagementModal";
import { authUrl, execute } from "@/api";
import moment from "moment";

export default {
  name: "EmployeeDashboard",
  components: {
    NavigationBar,
    UserManagementModal
  },
  data() {
    return {
      totalItems: 0,
      search: '',
      departmentFilter: '',
      statusFilter: '',
      typeFilter: '',
      loading: false,
      isDownloading: false,
      downloadSuccess: false,
      downloadError: false,
      currentPage: 1,
      totalPages: 0,
      pageSize: 20,
      employees: [],
      departments: [],
      activeEmployees: 0,
      resignedEmployees: 0,
      permanentEmployees: 0,
      contractEmployees: 0,
      internshipEmployees: 0,
      newEmployeesThisMonth: 0,
      averageTenure: 0,
      selectedEmployees: [],
      selectAll: false,
      searchTimeout: null,
      selectedEmployee: null
    }
  },
  watch: {
    pageSize(newPageSize) {
      this.getEmployees();
      const totalItems = this.totalItems;
      const newTotalPages = Math.ceil(totalItems / newPageSize);
      this.totalPages = newTotalPages;
      this.currentPage = 1;
    }
  },
  computed: {
    startIndex() {
      return (this.currentPage - 1) * this.pageSize;
    },
    endIndex() {
      return this.currentPage * this.pageSize;
    }
  },
  mounted() {
    this.getDepartments();
    this.getEmployees();
  },
  methods: {
    /**
     * NEW METHOD: Navigate to Category & Role Management
     */
    goToCategoryManagement() {
      this.$router.push('/category-role-management');
    },

    viewUserAccount(employee) {
      this.selectedEmployee = employee;
    },

    debounceSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.getEmployees();
      }, 500);
    },

    clearSearch() {
      this.search = '';
      this.currentPage = 1;
      this.getEmployees();
    },

    getInitials(firstName, lastName) {
      return ((firstName ? firstName.charAt(0) : '') + (lastName ? lastName.charAt(0) : '')).toUpperCase();
    },

    getFullName(item) {
      const parts = [item.firstName, item.middleName, item.lastName].filter(Boolean);
      return parts.join(' ') || 'N/A';
    },

    formatPhone(phone) {
      if (!phone) return 'N/A';
      if (phone.startsWith('254')) {
        return '+' + phone.slice(0, 3) + ' ' + phone.slice(3, 6) + ' ' + phone.slice(6);
      }
      return phone;
    },

    getEmploymentTypeClass(type) {
      const classes = {
        'Permanent': 'badge bg-success',
        'Temporary': 'badge bg-warning',
        'Internship': 'badge bg-info',
        'Contract': 'badge bg-secondary'
      };
      return classes[type] || 'badge bg-secondary';
    },

    getStatusClass(status) {
      const statusLower = status?.toLowerCase();
      if (statusLower === 'active') return 'badge bg-success';
      if (statusLower === 'resigned') return 'badge bg-danger';
      return 'badge bg-secondary';
    },

    getStatusText(status) {
      if (!status) return 'Unknown';
      return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    },

    getGenderIcon(gender) {
      const genderLower = gender?.toLowerCase();
      if (genderLower === 'male') return 'male';
      if (genderLower === 'female') return 'female';
      return 'male-female';
    },

    calculateTenure(hireDate) {
      if (!hireDate) return 'N/A';
      const years = moment().diff(moment(hireDate), 'years');
      const months = moment().diff(moment(hireDate), 'months') % 12;

      if (years > 0) {
        return `${years} year${years > 1 ? 's' : ''} ${months > 0 ? months + ' mo' : ''}`;
      } else if (months > 0) {
        return `${months} month${months > 1 ? 's' : ''}`;
      } else {
        return 'New hire';
      }
    },

    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedEmployees = this.employees.map(emp => emp.id);
      } else {
        this.selectedEmployees = [];
      }
    },

    resetFilters() {
      this.search = '';
      this.departmentFilter = '';
      this.statusFilter = '';
      this.typeFilter = '';
      this.currentPage = 1;
      this.getEmployees();
    },

    onFilterChange() {
      this.currentPage = 1;
      this.getEmployees();
    },

    pagination(currentPage, totalPages, maxVisibleButtons) {
      let pages = [];
      let start = currentPage - Math.floor(maxVisibleButtons / 2);
      let end = currentPage + Math.floor(maxVisibleButtons / 2);

      if (start < 1) {
        start = 1;
        end = start + maxVisibleButtons - 1;
        if (end > totalPages) {
          end = totalPages;
        }
      }
      if (end > totalPages) {
        end = totalPages;
        start = end - maxVisibleButtons + 1;
        if (start < 1) {
          start = 1;
        }
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (start > 1) {
        pages.unshift("...");
        pages.unshift(1);
      }

      if (end < totalPages) {
        pages.push("...");
        pages.push(totalPages);
      }

      return pages;
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.getEmployees();
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.getEmployees();
      }
    },

    goToPage(page) {
      if (typeof page === 'number' && page > 0 && page <= this.totalPages && page !== this.currentPage) {
        this.currentPage = page;
        this.getEmployees();
      }
    },

    editEmployee(item) {
      sessionStorage.setItem("employeeId", item.id);
      this.gotTo('employee-edit');
    },

    viewDetails(item) {
      sessionStorage.setItem("employeeId", item.id);
      this.gotTo('employee-details-view');
    },

    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.departments = res.data.data.departments || [];
            } else {
              if (this.$toast) {
                this.$toast.error("Failed to load departments: " + res.data.message);
              }
            }
          })
          .catch((e) => {
            if (this.$toast) {
              this.$toast.error("Failed to load departments: " + e.message);
            }
          });
    },

    getEmployees() {
      this.loading = true;
      this.selectedEmployees = [];
      this.selectAll = false;

      this.activeEmployees = 0;
      this.resignedEmployees = 0;
      this.permanentEmployees = 0;
      this.contractEmployees = 0;
      this.internshipEmployees = 0;
      this.newEmployeesThisMonth = 0;

      const data = new FormData();
      data.append("function", "getEmployees");
      data.append("page", this.currentPage);
      data.append("rows_per_page", this.pageSize);
      data.append("keyword", this.search);

      if (this.departmentFilter) {
        data.append("department", this.departmentFilter);
      }

      if (this.statusFilter) {
        data.append("status", this.statusFilter);
      }

      if (this.typeFilter) {
        data.append("employmentType", this.typeFilter);
      }

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              this.employees = res.data.data.employees;
              this.totalItems = res.data.data.totalItems;
              this.totalPages = Math.ceil(this.totalItems / this.pageSize);

              let totalTenure = 0;
              let tenureCount = 0;
              const currentMonth = moment().format('YYYY-MM');

              this.employees.forEach(emp => {
                if (emp.status?.toLowerCase() === 'active') {
                  this.activeEmployees++;
                }

                if (emp.employmentType === 'Permanent') {
                  this.permanentEmployees++;
                } else if (emp.employmentType === 'Contract') {
                  this.contractEmployees++;
                } else if (emp.employmentType === 'Internship') {
                  this.internshipEmployees++;
                }

                if (emp.hireDate && moment(emp.hireDate).format('YYYY-MM') === currentMonth) {
                  this.newEmployeesThisMonth++;
                }

                if (emp.hireDate && emp.status?.toLowerCase() === 'active') {
                  const tenure = moment().diff(moment(emp.hireDate), 'years', true);
                  totalTenure += tenure;
                  tenureCount++;
                }
              });

              if (tenureCount > 0) {
                this.averageTenure = (totalTenure / tenureCount).toFixed(1);
              }
            } else {
              if (this.$toast) {
                this.$toast.error(res.data.message);
              }
            }
          })
          .catch((e) => {
            this.loading = false;
            if (this.$toast) {
              this.$toast.error("Failed to load employees: " + e.message);
            }
          });
    },

    downloadEmployees() {
      if (this.isDownloading || this.employees.length === 0) return;

      this.isDownloading = true;

      setTimeout(() => {
        try {
          let filename = 'Employee_Report';

          if (this.departmentFilter) {
            const selectedDept = this.departments.find(d => d.id === this.departmentFilter);
            if (selectedDept) {
              filename += '_' + selectedDept.departmentName.replace(/\s+/g, '');
            }
          }

          if (this.statusFilter) {
            filename += '_' + this.statusFilter.charAt(0).toUpperCase() + this.statusFilter.slice(1);
          }

          if (this.typeFilter) {
            filename += '_' + this.typeFilter;
          }

          filename += '_' + new Date().toISOString().slice(0, 10);

          let csvContent = "data:text/csv;charset=utf-8,";

          const headers = [
            "Employee ID", "Employee No", "Full Name", "First Name", "Middle Name", "Last Name",
            "Email", "Phone", "National ID", "Department", "Department ID", "Employment Type",
            "Status", "Gender", "Date of Birth", "Nationality", "Hire Date", "Tenure",
            "Created At", "Updated At"
          ];

          csvContent += headers.join(",") + "\r\n";

          this.employees.forEach(emp => {
            const fullName = this.getFullName(emp);
            const tenure = this.calculateTenure(emp.hireDate);
            const row = [
              emp.id || '', emp.empNo || '', `"${fullName}"`, `"${emp.firstName || ''}"`,
              `"${emp.middleName || ''}"`, `"${emp.lastName || ''}"`, `"${emp.email || ''}"`,
              `"${emp.phone || ''}"`, `"${emp.nationalId || ''}"`, `"${emp.departmentName || ''}"`,
              emp.departmentID || '', `"${emp.employmentType || ''}"`, `"${this.getStatusText(emp.status)}"`,
              `"${emp.gender || ''}"`, `"${emp.dateOfBirth || ''}"`, `"${emp.nationality || ''}"`,
              `"${emp.hireDate || ''}"`, `"${tenure}"`, `"${emp.createdAt || ''}"`, `"${emp.updatedAt || ''}"`
            ];

            csvContent += row.join(",") + "\r\n";
          });

          const encodedUri = encodeURI(csvContent);
          const link = document.createElement("a");
          link.setAttribute("href", encodedUri);
          link.setAttribute("download", filename + ".csv");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          if (this.$toast) {
            this.$toast.success("Report exported successfully");
          }
        } catch (error) {
          if (this.$toast) {
            this.$toast.error("Failed to export report: " + error.message);
          }
        } finally {
          this.isDownloading = false;
        }
      }, 800);
    },

    gotTo(route) {
      this.$router.push(route);
    },

    formatDate(date) {
      if (!date) return 'N/A';
      let d = new Date(date);
      let day = d.getDate();
      let month = d.toLocaleString('en-US', { month: 'short' });
      let year = d.getFullYear();
      return `${day} ${month} ${year}`;
    },

    humanDateTimeAgo(date) {
      return moment(date).fromNow();
    }
  }
}
</script>

<style scoped>
.badge {
  font-weight: 500;
  padding: 0.4em 0.6em;
  font-size: 0.75rem;
}

.table th, .table td {
  vertical-align: middle;
  padding: 0.75rem;
}

.avatar-sm {
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.search-box .form-control {
  padding-left: 40px;
  padding-right: 80px;
}

.search-box .search-btn {
  position: absolute;
  right: 40px;
  top: 50%;
  transform: translateY(-50%);
  padding: 0;
  color: #6c757d;
  text-decoration: none;
  border: none;
  background: none;
}

.search-box .search-btn:hover {
  color: #0d6efd;
}

.search-box .clear-btn {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  padding: 0;
  color: #dc3545;
  text-decoration: none;
  border: none;
  background: none;
}

.search-box .clear-btn:hover {
  color: #b02a37;
}

.search-box .search-icon {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  pointer-events: none;
  z-index: 10;
}

.search-icon .spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.search-box .form-control.is-loading {
  background-color: #f8f9fa !important;
}

.table-hover tbody tr:hover {
  background-color: rgba(0, 123, 255, 0.05);
  cursor: pointer;
}

.table-selected {
  background-color: rgba(0, 123, 255, 0.1) !important;
}

.dropdown-toggle::after {
  display: none;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
}

.bg-gradient {
  background-image: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%);
}

/* NEW: Category & Roles button styling */
.btn-info {
  background-color: #17a2b8;
  border-color: #17a2b8;
  color: white;
}

.btn-info:hover {
  background-color: #138496;
  border-color: #117a8b;
}

@media (max-width: 768px) {
  .search-box {
    margin-bottom: 1rem;
  }

  .btn-toolbar {
    flex-direction: column;
  }

  .table-responsive {
    font-size: 0.875rem;
  }

  .input-group {
    margin-bottom: 0.5rem;
  }
}

.card {
  border-radius: 0.75rem;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.05);
  border: none;
}

.card-header {
  border-radius: 0.75rem 0.75rem 0 0;
}

.badge {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.footer {
  background-color: #f8f9fa;
  padding: 20px 0;
  border-top: 1px solid #e9ecef;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.mdi-loading {
  animation: spin 1s linear infinite;
}

.form-check-input {
  cursor: pointer;
}

.form-check-input:checked {
  background-color: #0d6efd;
  border-color: #0d6efd;
}
</style>